import { RouterAwareLink } from './RouterAwareLink'
import { Slot } from './Slot'
import { cn, tv } from '@joyful/utils'
import type { ReactElement } from 'react'
import type { LinkProps } from 'react-router-dom'
import type { VariantProps } from '@joyful/utils'

export const textLinkVariants = tv({
  base: 'inline-flex items-center gap-1 underline-offset-2 hover:decoration-2',

  defaultVariants: {
    colorVariant: 'default',
    size: 'inherit',
    underlineVariant: 'always',
    weight: 'inherit',
  },

  slots: {
    icon: 'size-4',
  },

  variants: {
    colorVariant: {
      brand: {
        base: 'text-tremor-brand',
      },
      default: {
        base: 'text-tremor-content-strong',
      },
      emphasis: {
        base: 'text-tremor-content-emphasis',
      },
      inherit: {
        base: 'text-inherit',
      },
      muted: {
        base: 'text-tremor-content',
      },
    },

    /** `inline` flows as running text, so an `Ellipsis` around the link can truncate it. That `Ellipsis` needs `leading="normal"`, or its clip hides the hover underline. Icons need the default. */
    flow: {
      inline: {
        base: 'inline',
      },
    },

    size: {
      inherit: {},
      sm: {
        base: 'text-sm',
      },
      xs: {
        base: 'text-xs',
      },
    },

    underlineVariant: {
      always: {
        base: 'underline',
      },
      hover: {
        base: 'hover:underline',
      },
    },

    weight: {
      inherit: {},
      medium: {
        base: 'font-medium',
      },
      semibold: {
        base: 'font-semibold',
      },
    },
  },
})

export interface TextLinkProps
  extends Omit<LinkProps, 'className'>, VariantProps<typeof textLinkVariants> {
  endIcon?: ReactElement
  startIcon?: ReactElement
}

export function TextLink({
  children,
  className,
  colorVariant,
  endIcon,
  flow,
  size,
  startIcon,
  to,
  underlineVariant,
  weight,
  ...props
}: TextLinkProps & { className?: string }) {
  const isExternal = typeof to === 'string' && to.startsWith('http') === true
  const classNames = textLinkVariants({
    colorVariant,
    flow,
    size,
    underlineVariant,
    weight,
  })

  return (
    <RouterAwareLink
      to={to}
      {...props}
      className={cn(classNames.base(), className)}
      target={isExternal ? '_blank' : props.target}
      rel={isExternal ? 'noopener noreferrer' : props.rel}
    >
      {startIcon ? (
        <Slot className={classNames.icon()}>{startIcon}</Slot>
      ) : null}
      {children}
      {endIcon ? <Slot className={classNames.icon()}>{endIcon}</Slot> : null}
    </RouterAwareLink>
  )
}
